import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import useApi from "../../CustomHooks/useApi";
import Popup from "../../ResuableCompoment/Popup";
import DocumentViewer from "./DocumentViewer";

const DocumentDetail = () => {
  const [zoom, setZoom] = useState(1);
  const location = useLocation();
  const formData = location.state?.form || {};

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [responses, setResponses] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState({});
  const [submittedRejections, setSubmittedRejections] = useState({});

  /* ✅ NEW STATES */
  const [inlineErrors, setInlineErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const { callApi: Approveapi } = useApi(
    "/staff/onboard/compliance/approval",
    "POST"
  );

  const handleDoubleClick = () => setZoom((z) => (z === 1 ? 1.5 : 1));

  const documentNames = [
    "aadhaarBack",
    "aadhaarFront",
    "panCard",
    "passbookOrChequeBook",
    "selfieWithDocuments",
    "selfieWithSalesPerson",
    "shopPhoto",
    "signaturePhoto",
    "Biometricdevice",
    "gstcertificate",
    "VideoaadhaarBack",
    "VideoaadhaarFront",
    "VideopanCard",
    "VideoshopPhoto",
    "optional1",
    "optional2",
  ];

  const usedUrls = new Set();
  const filesUploaded = formData.filesUploaded || [];

  const documents = documentNames
    .map((docName) => {
      const regex = new RegExp(`[\\/\\-]${docName}(\\?|$)`, "i");
      const matched = filesUploaded.find(
        (url) => regex.test(url) && !usedUrls.has(url)
      );

      const fallback = formData.documents?.[docName];
      const finalUrl = matched || fallback;

      if (finalUrl) {
        usedUrls.add(finalUrl);
        return {
          name: docName.replace(/_/g, " ").toUpperCase(),
          url: finalUrl.replace(/\\/g, "/"),
        };
      }
      return null;
    })
    .filter(Boolean);

  /* ================= RESPONSE HANDLERS ================= */
  const handleResponse = (documentName, documentUrl, status) => {
    setInlineErrors((p) => ({ ...p, [documentName]: "" }));

    const idx = responses.findIndex((r) => r.documentName === documentName);
    if (idx !== -1) {
      const updated = [...responses];
      updated[idx] = { documentName, documentUrl, status };
      setResponses(updated);
    } else {
      setResponses([...responses, { documentName, documentUrl, status }]);
    }
  };

  const handleReasonChange = (documentName, reason) => {
    setRejectionReasons((p) => ({ ...p, [documentName]: reason }));
    setInlineErrors((p) => ({ ...p, [documentName]: "" }));

    setResponses((prev) =>
      prev.map((d) =>
        d.documentName === documentName ? { ...d, reason } : d
      )
    );
  };

  const handleSubmitSingle = (docName) => {
    if (!rejectionReasons[docName]) {
      setInlineErrors((p) => ({
        ...p,
        [docName]: "Rejection reason is required",
      }));
      return;
    }

    setResponses((prev) => [
      ...prev.filter((r) => r.documentName !== docName),
      {
        documentName: docName,
        status: "Rejected",
        reason: rejectionReasons[docName],
        url: selectedDoc.url,
      },
    ]);

    setSubmittedRejections((p) => ({ ...p, [docName]: true }));
  };

  const handleSubmit = async () => {
    if (responses.length !== documents.length) {
      setSubmitError("Please review all documents before submitting.");
      return;
    }

    setSubmitError("");

    const hasRejected = responses.some((d) => d.status === "Rejected");

    const payload = {
      mobile_number: formData?.mobileNumber,
      user_id: formData?.userId,
      docId: "doc789",
      action: hasRejected ? "REJECT" : "APPROVE",
      reason: hasRejected
        ? responses
          .filter((d) => d.status === "Rejected")
          .map((d) => `${d.documentName}: ${d.reason}`)
          .join(", ")
        : "Verified successfully",
    };

    try {
      const res = await Approveapi(payload);
      if (res) {
        setPopupMessage("Documents reviewed and submitted successfully.");
        setShowPopup(true);
      }
    } catch {
      setPopupMessage("Submission failed. Please try again.");
      setShowPopup(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F6FA] p-6">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-7xl mx-auto bg-white rounded-xl shadow-xl p-6"
      >

        {/* ================= BASIC DETAILS ================= */}
        <table className="w-full mb-6 border rounded-lg overflow-hidden">
          <thead className="bg-[#0A2A4F] text-white">
            <tr>
              <th className="px-6 py-3 text-left">Category</th>
              <th className="px-6 py-3 text-left">Details</th>
            </tr>
          </thead>
          <tbody>
            {/* BASIC */}
            <tr className="bg-[#F0F3F9]">
              <td colSpan="2" className="px-6 py-3 font-semibold text-[#0A2A4F]">
                Basic Details
              </td>
            </tr>
            <tr><td className="px-6 py-3">Full Name</td><td className="px-6 py-3">{formData?.meta?.fullName || "-"}</td></tr>
            <tr><td className="px-6 py-3">PAN</td><td className="px-6 py-3">{formData?.pan || "-"}</td></tr>
            <tr><td className="px-6 py-3">DOB</td><td className="px-6 py-3">{formData?.meta?.dob || "-"}</td></tr>
            {/* AADHAAR */}
            <tr className="bg-[#F0F3F9]">
              <td colSpan="2" className="px-6 py-3 font-semibold text-[#0A2A4F]">
                Aadhaar Details
              </td>
            </tr>
            <tr><td className="px-6 py-3">Aadhaar Number</td><td className="px-6 py-3">{formData?.meta?.aadharNumber || "-"}</td></tr>
            <tr><td className="px-6 py-3">Address</td><td className="px-6 py-3">{formData?.meta?.aadharAddress || "-"}</td></tr>

            {/* BANK */}
            <tr className="bg-[#F0F3F9]">
              <td colSpan="2" className="px-6 py-3 font-semibold text-[#0A2A4F]">
                Bank Account Details
              </td>
            </tr>
            <tr><td className="px-6 py-3">Account No</td><td className="px-6 py-3">{formData?.meta?.accountNumber || "-"}</td></tr>
            <tr><td className="px-6 py-3">Bank Name</td><td className="px-6 py-3">{formData?.meta?.bankName || "-"}</td></tr>
            <tr><td className="px-6 py-3">IFSC</td><td className="px-6 py-3">{formData?.meta?.ifscCode || "-"}</td></tr>

            {/* SHOP */}
            <tr className="bg-[#F0F3F9]">
              <td colSpan="2" className="px-6 py-3 font-semibold text-[#0A2A4F]">
                Shop Details
              </td>
            </tr>
            <tr><td className="px-6 py-3">Shop Name</td><td className="px-6 py-3">{formData?.meta?.shopName || "-"}</td></tr>
            <tr><td className="px-6 py-3">Shop Address</td><td className="px-6 py-3">{formData?.meta?.shopAddress || "-"}</td></tr>
            <tr><td className="px-6 py-3">GST Number</td><td className="px-6 py-3">{formData?.meta?.gst || "-"}</td></tr>
            <tr><td className="px-6 py-3">GST Trade Name</td><td className="px-6 py-3">{formData?.meta?.gstTradeName || "-"}</td></tr>
          </tbody>
        </table>

        {/* ================= DOCUMENT REVIEW ================= */}
        <div className="flex gap-4 h-[600px]">
          {/* LEFT */}
          <div className="w-1/2 border rounded-lg overflow-y-auto">
            <table className="w-full">
              <thead className="bg-[#0A2A4F] text-white">
                <tr>
                  <th className="px-6 py-3 text-left">Document</th>
                  <th className="px-6 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const res = responses.find(
                    (r) => r.documentName === doc.name
                  );
                  return (
                    <tr key={doc.name} className="border-b">
                      <td className="px-6 py-3">{doc.name}</td>
                      <td className="px-6 py-3 text-center">
                        <button
                          onClick={() => setSelectedDoc(doc)}
                          className="text-[#0A2A4F] hover:underline"
                        >
                          View
                        </button>
                        {res?.status === "Accepted" && (
                          <span className="ml-2 text-green-600">●</span>
                        )}
                        {res?.status === "Rejected" && (
                          <span className="ml-2 text-red-500">●</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* RIGHT */}
          <div className="w-1/2 border rounded-lg p-4 flex flex-col">






           {selectedDoc ? (
  <>
    <h3 className="font-semibold text-[#0A2A4F] mb-2">
      {selectedDoc.name}
    </h3>

    {/* DOCUMENT VIEWER */}
    <div className="h-[60vh] w-full border rounded overflow-hidden">
      <DocumentViewer url={selectedDoc.url} />
    </div>

    {/* ACCEPT BUTTON */}
    <button
      onClick={() =>
        handleResponse(
          selectedDoc.name,
          selectedDoc.url,
          "Accepted"
        )
      }
      className="mt-3 bg-[#0A2A4F] text-white py-2 rounded w-full"
    >
      Accept
    </button>

    {/* REJECT INPUT */}
    <input
      className="mt-3 border rounded px-3 py-2 w-full"
      placeholder="Reason for rejection"
      value={rejectionReasons[selectedDoc.name] || ""}
      onChange={(e) =>
        handleReasonChange(selectedDoc.name, e.target.value)
      }
    />

    {/* INLINE ERROR */}
    {inlineErrors[selectedDoc.name] && (
      <p className="text-sm text-red-500 mt-1">
        {inlineErrors[selectedDoc.name]}
      </p>
    )}

    {/* SUBMIT REJECTION */}
    <button
      onClick={() => handleSubmitSingle(selectedDoc.name)}
      className="mt-2 bg-red-500 text-white py-2 rounded w-full"
    >
      Submit Rejection
    </button>
  </>
) : (
  <p className="text-center text-gray-400">
    Select a document to view
  </p>
)}





          </div>
        </div>

        {/* ✅ FINAL SUBMIT ERROR ABOVE BUTTON */}
        {submitError && (
          <p className="text-center text-red-500 mt-4">{submitError}</p>
        )}

        <div className="mt-4 text-center">
          <button
            onClick={handleSubmit}
            className="bg-[#0A2A4F] text-white px-8 py-3 rounded-xl font-semibold"
          >
            Submit Responses
          </button>
        </div>
      </motion.div>

      {/* ✅ POPUP */}
      {showPopup && (
        <Popup
          message={popupMessage}
          onClose={() => setShowPopup(false)}
          navigateTo="/kycdashboard"
        />
      )}
    </div>
  );
};

export default DocumentDetail;




